'use client';

import { trackEvent } from '@/components/providers';
import { queryKeys } from '@/lib/query-keys';
import { apiClient, isTimeoutError } from '@borradh-workspace/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

/** Only what the UI reads; the server also returns the saved ad row. */
interface PublishAdResponse {
  alreadyLive?: boolean;
  /** Set here, not by the server: the request outlived its timeout. */
  stillLaunching?: boolean;
}

/**
 * Above the server's worst case: `uploadMediaToMeta` alone waits up to 180 s
 * for Meta to encode a video, before the creative, ad and activation calls.
 */
const PUBLISH_TIMEOUT_MS = 300_000;

/**
 * Launch an ad that has never reached Meta: a draft, or an `error` ad whose
 * earlier launch failed before Meta accepted it. The server refuses anything
 * else, and an ad that already exists on Meta comes back `alreadyLive` rather
 * than being created twice.
 *
 * Synchronous on the server (upload, creative, ad, activation). If the request
 * still times out, the server has claimed the ad and carries on: that is
 * reported as "still launching", never as a failure, so nobody retries or
 * rebuilds an ad that is about to go live.
 */
export const usePublishAd = (toastOnSuccess = true, toastOnError = true) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (adId: string): Promise<PublishAdResponse> => {
      try {
        return await apiClient.post<PublishAdResponse>(
          `meta-ads/${adId}/publish`,
          undefined,
          { timeout: PUBLISH_TIMEOUT_MS }
        );
      } catch (error) {
        if (isTimeoutError(error)) return { stillLaunching: true };
        throw error;
      }
    },
    onSuccess: (result) => {
      // Same event as the wizard's launch, so the funnel counts relaunches too.
      // A timed-out request is counted: the server has claimed the ad and goes on.
      if (!result?.alreadyLive) trackEvent('ad_launched');
      void queryClient.invalidateQueries({ queryKey: queryKeys.metaAds.all() });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.metaAds.campaigns(),
      });
      if (!toastOnSuccess) return;
      if (result?.stillLaunching) {
        toast.info(
          'Still launching: Meta is processing the ad. Its status updates here when it finishes.'
        );
      } else {
        toast.success(
          result?.alreadyLive ? 'This ad is already on Meta' : 'Ad launched'
        );
      }
    },
    onError: (error) => {
      // Refetch so the row shows the reason the server saved for this attempt.
      void queryClient.invalidateQueries({ queryKey: queryKeys.metaAds.all() });
      if (toastOnError) toast.error(`Failed to launch ad: ${error.message}`);
    },
  });

  return {
    ...mutation,
    execute: mutation.mutate,
    executeAsync: mutation.mutateAsync,
    isExecuting: mutation.isPending,
  };
};
